import createGenericItem from './createGenericItem';
import { populateFeedbackStateCache, resetFeedbackState } from './populateFeedbackState';
import updateCurrentRenderedQueryString from '../actions/updateCurrentRenderedQueryString';
import updateQueryString from '../actions/updateQueryString';
import FindResultRecipient from '../components/FindResultRecipient';
import getSuggestionProps from '../utils/getSuggestionProps';
import { ITheme } from '@uifabric/styling';
import pauseSession from 'owa-controls-findpeople-feedback-manager/lib/actions/pauseSession';
import resumeSession from 'owa-controls-findpeople-feedback-manager/lib/actions/resumeSession';
import { now } from 'owa-datetime';
import findReadWriteRecipient from 'owa-readwrite-recipient-well/lib/actions/findReadWriteRecipient';
import updateFindResultTransactionId from 'owa-readwrite-recipient-well/lib/actions/updateFindResultTransactionId';
import { maskAutoCompleteRecipient } from 'owa-readwrite-recipient-well/lib/services/maskAutoCompleteRecipient';
import isValidEmailAddress from 'owa-readwrite-recipient-well/lib/utils/isValidEmailAddress';
import getAndPopulateSuggestedContacts from 'owa-recipient-cache/lib/actions/getAndPopulateSuggestedContacts';
import deleteFromCache from 'owa-recipient-cache/lib/selectors/deleteFromCache';
import FindControlViewState from 'owa-recipient-common/lib/store/schema/FindControlViewState';
import FindRecipientPersonaType from 'owa-recipient-common/lib/store/schema/FindRecipientPersonaType';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import addLocalCacheTransactionComplete from 'owa-recipient-common/lib/utils/addLocalCacheTransactionComplete';
import getPersonasFromReadWriteRecipientList from 'owa-recipient-common/lib/utils/getPersonasFromReadWriteRecipientList';
import {
    FloatingPeoplePicker,
    IPeopleFloatingPickerProps,
    SuggestionsStore,
} from 'office-ui-fabric-react/lib/FloatingPicker';

export default function getFloatingPickerProps(
    getViewState: () => FindControlViewState,
    getFindControl: () => FloatingPeoplePicker,
    controlledComponent: boolean,
    theme?: ITheme,
    onPickerHidden?: () => void
): IPeopleFloatingPickerProps {
    return {
        suggestionsStore: new SuggestionsStore<FindRecipientPersonaType>(),
        onValidateInput: isValidEmailAddress,
        onRenderSuggestionsItem: onRenderSuggestion(
            getViewState,
            getFindControl,
            controlledComponent,
            theme
        ),
        createGenericItem: createGenericItem,
        onResolveSuggestions: controlledComponent
            ? getQuerySuggestions(getViewState())
            : getQuerySuggestionsSynchronous(getViewState()),
        onZeroQuerySuggestion: controlledComponent
            ? getZeroQuerySuggestions(getViewState())
            : getZeroQuerySuggestionsSynchronous(getViewState()),
        calloutWidth: 300,
        resolveDelay: 150,
        onRemoveSuggestion: onRemoveSuggestion,
        getTextFromItem: (item: FindRecipientPersonaType) => {
            return item.EmailAddress.Name;
        },
        pickerSuggestionsProps: getSuggestionProps(
            getViewState,
            getFindControl,
            controlledComponent,
            theme
        ),
        onSuggestionsShown: onSuggestionsShown(getViewState()),
        onSuggestionsHidden: onSuggestionsHidden(getViewState(), onPickerHidden),
    };
}

function onRemoveSuggestion(item: FindRecipientPersonaType): void {
    deleteFromCache(item.EmailAddress);
    maskAutoCompleteRecipient(item.EmailAddress.EmailAddress);
}

function onRenderSuggestion(
    getViewState: () => FindControlViewState,
    getFindControl: () => FloatingPeoplePicker,
    controlledComponent: boolean,
    theme: ITheme
): (props: FindRecipientPersonaType, itemProps: any) => JSX.Element {
    return function(props: FindRecipientPersonaType, itemProps: any): JSX.Element {
        return FindResultRecipient({
            viewState: getViewState(),
            itemProps: itemProps,
            persona: props,
            onMaskRecipient: onMaskRecipient(getFindControl(), controlledComponent),
            theme: theme,
        });
    };
}

function onMaskRecipient(
    findControl: FloatingPeoplePicker,
    controlledComponent: boolean
): (viewState: FindControlViewState) => void {
    return function(viewState: FindControlViewState): void {
        if (!controlledComponent) {
            findControl.updateSuggestions(viewState.findResultSet.slice(), true /*updateUI*/);
        }
    };
}

/* This won't be needed after vso: 35556 */ function getQuerySuggestionsSynchronous(
    viewState: FindControlViewState
): (
    filterText: string,
    recipients: ReadWriteRecipientViewState[]
) => Promise<FindRecipientPersonaType[]> {
    return async function(filterText: string, recipients: ReadWriteRecipientViewState[]) {
        if (viewState.queryString === filterText) {
            await findReadWriteRecipient(viewState, filterText, false /*searchDirectory*/);
            return viewState.findResultSet.slice();
        }

        return null;
    };
}

function getQuerySuggestions(
    viewState: FindControlViewState
): (filterText: string, recipients: ReadWriteRecipientViewState[]) => FindRecipientPersonaType[] {
    return function(filterText: string, recipients: ReadWriteRecipientViewState[]) {
        if (viewState.queryString === filterText) {
            findReadWriteRecipient(viewState, filterText, false /*searchDirectory*/);
        }
        return null;
    };
}

/* This won't be needed after vso: 35556 */ function getZeroQuerySuggestionsSynchronous(
    viewState: FindControlViewState
): (recipients: ReadWriteRecipientViewState[]) => Promise<FindRecipientPersonaType[]> {
    return async function(recipients: ReadWriteRecipientViewState[]) {
        updateQueryString(viewState, '');
        updateCurrentRenderedQueryString(viewState, '');
        const transactionStartTime = now();
        resetFeedbackState(viewState);
        getAndPopulateSuggestedContacts(
            viewState.findResultSet,
            getPersonasFromReadWriteRecipientList(recipients)
        );

        populateFeedbackStateCache(viewState, transactionStartTime, viewState.findResultSet);
        updateFindResultTransactionId(viewState);
        addLocalCacheTransactionComplete(viewState);
        return viewState.findResultSet.slice();
    };
}

function getZeroQuerySuggestions(
    viewState: FindControlViewState
): (recipients: ReadWriteRecipientViewState[]) => FindRecipientPersonaType[] {
    return function(recipients: ReadWriteRecipientViewState[]) {
        updateQueryString(viewState, '');
        updateCurrentRenderedQueryString(viewState, '');
        const transactionStartTime = now();
        resetFeedbackState(viewState);
        getAndPopulateSuggestedContacts(
            viewState.findResultSet,
            getPersonasFromReadWriteRecipientList(recipients)
        );
        populateFeedbackStateCache(viewState, transactionStartTime, viewState.findResultSet);
        updateFindResultTransactionId(viewState);
        addLocalCacheTransactionComplete(viewState);
        return null;
    };
}

function onSuggestionsShown(viewState: FindControlViewState): () => void {
    return function() {
        resumeSession(viewState.findPeopleFeedbackManager);
    };
}

function onSuggestionsHidden(
    viewState: FindControlViewState,
    onPickerHidden?: () => void
): () => void {
    return function() {
        onPickerHidden && onPickerHidden();
        pauseSession(viewState.findPeopleFeedbackManager);
    };
}
